import { useCallback, useRef, UIEvent } from 'react';
import { ScrollPosition } from '../ScrollArea.types';

const BOUNDARY_TOLERANCE = 0.8;

interface UseScrollPositionOptions {
  onScrollPositionChange?: (position: ScrollPosition) => void;
  onTopReached?: () => void;
  onBottomReached?: () => void;
  onLeftReached?: () => void;
  onRightReached?: () => void;
}

interface UseScrollPositionResult {
  handleScroll: (event: UIEvent<HTMLElement>) => void;
}

export const useScrollPosition = (options: UseScrollPositionOptions): UseScrollPositionResult => {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const prevAtTopRef = useRef(true);
  const prevAtBottomRef = useRef(false);
  const prevAtLeftRef = useRef(true);
  const prevAtRightRef = useRef(false);

  const handleScroll = useCallback((event: UIEvent<HTMLElement>) => {
    const { onScrollPositionChange, onTopReached, onBottomReached, onLeftReached, onRightReached } = optionsRef.current;
    const target = event.currentTarget;
    const { scrollTop, scrollLeft, scrollHeight, scrollWidth, clientHeight, clientWidth } = target;

    onScrollPositionChange?.({ x: scrollLeft, y: scrollTop });

    const atTop = scrollTop <= BOUNDARY_TOLERANCE;
    const atBottom = Math.abs(scrollHeight - scrollTop - clientHeight) <= BOUNDARY_TOLERANCE;
    const atLeft = scrollLeft <= BOUNDARY_TOLERANCE;
    const atRight = Math.abs(scrollWidth - scrollLeft - clientWidth) <= BOUNDARY_TOLERANCE;

    if (atTop && !prevAtTopRef.current) onTopReached?.();
    if (atBottom && !prevAtBottomRef.current) onBottomReached?.();
    if (atLeft && !prevAtLeftRef.current) onLeftReached?.();
    if (atRight && !prevAtRightRef.current) onRightReached?.();

    prevAtTopRef.current = atTop;
    prevAtBottomRef.current = atBottom;
    prevAtLeftRef.current = atLeft;
    prevAtRightRef.current = atRight;
  }, []);

  return { handleScroll };
};
